import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ErrorWithData} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dialog} from '../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogHeader} from '../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildRepeatingAvailabilitySummary,
  buildStandardAvailabilitySummary,
} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {CalendarWeekDaysEnum} from '../../../../models/api/calendarCommonTypes';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkRepeatingAvailabilityBlockModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../models/api/schedulingLinkModel';
import {FilterDefinition, SegmentedFilters} from '../../../common/segmentedFilters/segmentedFilters';
import {RecurringAvailabilityCalendarContainer} from '../availability/recurringAvailabilityCalendarContainer';
import {StandardAvailabilityCalendarContainer} from '../availability/standardAvailabilityCalendarContainer';

/*
 * Types.
 */

interface SchedulingLinkSettingsCalendarDialogProps {
  panelId: CalendarsPanelId;
  onRequestClose: () => void;
  draftLink: DraftSchedulingLinkModel;
  weekStartDay: CalendarWeekDaysEnum;
  initialStandardSlots: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>;
  initialRepeatingSlots: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>;
  onUpdateStandardSlots: (arg: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>) => void;
  onUpdateRepeatingSlots: (arg: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>) => void;
  isPopout: boolean;
  hasOnlyRecurringDialog?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  flex-grow: 1;
  overflow: auto;
`;

const StyledFilterItem = styled.div`
  padding: 4px 10px;
`;

const StyledFooterDiv = styled.div`
  display: grid;
  justify-content: end;
  grid-column-gap: 10px;
  grid-auto-flow: column;

  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 12px 16px;
`;

const StyledSummaryDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledSummaryTextDiv = styled.div`
  padding: 7px 0px 6px 5px;
`;

/*
 * Component.
 */

export const SchedulingLinkSettingsCalendarDialog: FC<SchedulingLinkSettingsCalendarDialogProps> = (
  props,
) => {
  const theme = useUserTheme();
  const {
    draftLink,
    initialStandardSlots,
    initialRepeatingSlots,
    onUpdateStandardSlots,
    onUpdateRepeatingSlots,
    onRequestClose,
    isPopout,
    hasOnlyRecurringDialog,
  } = props;

  const [availabilityType, setAvailabilityType] = useState(draftLink.availability.type);
  const [standardAvailability, setStandardAvailability] = useState(initialStandardSlots);
  const [repeatingAvailability, setRepeatingAvailability] = useState(initialRepeatingSlots);

  const filters = [
    {
      id: SchedulingLinkAvailabilityTypesEnum.REPEATING,
      content: (
        <StyledFilterItem>
          <Trans id="scheduling-settings-recurring">Recurring</Trans>
        </StyledFilterItem>
      ),
      onClick: () => setAvailabilityType(SchedulingLinkAvailabilityTypesEnum.REPEATING),
    },
    {
      id: SchedulingLinkAvailabilityTypesEnum.STANDARD,
      content: (
        <StyledFilterItem>
          <Trans id="scheduling-settings-standard">Standard</Trans>
        </StyledFilterItem>
      ),
      onClick: () => setAvailabilityType(SchedulingLinkAvailabilityTypesEnum.STANDARD),
    },
  ];

  const isAvailabilitySelected =
    availabilityType === SchedulingLinkAvailabilityTypesEnum.REPEATING
      ? repeatingAvailability.length > 0
      : standardAvailability.length > 0;

  const onSaveAvailability = useCallback(() => {
    if (availabilityType === SchedulingLinkAvailabilityTypesEnum.REPEATING) {
      onUpdateRepeatingSlots(repeatingAvailability);
    } else {
      onUpdateStandardSlots(standardAvailability);
    }

    onRequestClose();
  }, [
    onRequestClose,
    availabilityType,
    standardAvailability,
    repeatingAvailability,
    onUpdateStandardSlots,
    onUpdateRepeatingSlots,
  ]);

  const onClearAvailability = useCallback(() => {
    switch (availabilityType) {
      case SchedulingLinkAvailabilityTypesEnum.REPEATING:
        setRepeatingAvailability([]);
        break;
      case SchedulingLinkAvailabilityTypesEnum.STANDARD:
        setStandardAvailability([]);
        break;
      default:
        break;
    }
  }, [availabilityType]);

  return (
    <Dialog
      onRequestClose={onRequestClose}
      minWidth={isPopout ? '95vw' : '80vw'}
      minHeight={isPopout ? '95vh' : '80vh'}
      height={300}
    >
      <StyledWrapperDiv>
        {renderDialogHeader(onRequestClose, filters, availabilityType, hasOnlyRecurringDialog)}
        {renderCalendar(
          props,
          availabilityType,
          repeatingAvailability,
          setRepeatingAvailability,
          standardAvailability,
          setStandardAvailability,
        )}
        <StyledFooterDiv>
          {isAvailabilitySelected && (
            <>
              <StyledSummaryDiv>
                <Icon name="checkmarkYesActivity" color={theme.palette.green.shade40} />
                <StyledSummaryTextDiv>
                  {availabilityType === SchedulingLinkAvailabilityTypesEnum.STANDARD &&
                    buildStandardAvailabilitySummary(standardAvailability)}
                  {availabilityType === SchedulingLinkAvailabilityTypesEnum.REPEATING &&
                    buildRepeatingAvailabilitySummary(repeatingAvailability, props.weekStartDay)}
                </StyledSummaryTextDiv>
              </StyledSummaryDiv>
              <Button variant={ButtonVariantsEnum.LINK} onClick={onClearAvailability}>
                <Trans id="scheduling-settings-cleartimes">Clear all</Trans>
              </Button>
            </>
          )}
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSaveAvailability}>
            <Trans id="scheduling-settings-savetimes">Save times</Trans>
          </Button>
        </StyledFooterDiv>
      </StyledWrapperDiv>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function renderDialogHeader(
  onRequestClose: () => void,
  filters: ReadonlyArray<FilterDefinition>,
  availabilityType: SchedulingLinkAvailabilityTypesEnum,
  hasOnlyRecurringDialog?: boolean,
) {
  if (hasOnlyRecurringDialog) {
    return (
      <DialogHeader
        onRequestClose={onRequestClose}
        title={<Trans id="scheduling-settings-calendar-title">Edit times</Trans>}
      />
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <DialogHeader
          onRequestClose={onRequestClose}
          title={
            <SegmentedFilters
              filters={filters}
              selectedFilterIndex={filters.findIndex((filter) => filter.id === availabilityType)}
              color={theme.greys.shade70}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCalendar(
  props: SchedulingLinkSettingsCalendarDialogProps,
  availabilityType: SchedulingLinkAvailabilityTypesEnum,
  repeatingAvailability: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>,
  setRepeatingAvailability: (arg: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>) => void,
  standardAvailability: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>,
  setStandardAvailability: (arg: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>) => void,
) {
  switch (availabilityType) {
    case SchedulingLinkAvailabilityTypesEnum.REPEATING:
      return (
        <RecurringAvailabilityCalendarContainer
          selectedSlots={repeatingAvailability}
          setSelectedSlots={setRepeatingAvailability}
        />
      );

    case SchedulingLinkAvailabilityTypesEnum.STANDARD:
      return (
        <StandardAvailabilityCalendarContainer
          panelId={props.panelId}
          renderRightPanel={() => null}
          selectedSlots={standardAvailability}
          setSelectedSlots={setStandardAvailability}
        />
      );

    default:
      reportToBugsnag(new ErrorWithData('Unknown Availability Type', {type: availabilityType}));
      return null;
  }
}
